const getUniqueSpecialization = (
  colleges: any[],
  filterOptions: any,
  filterSecStreamData: any
): any[] => {
  // Return empty array if filterOptions is null/undefined
  if (!(filterOptions?.stream && filterOptions?.course)) {
    return [];
  }

  // Create sets for both primary and secondary data
  const primarySpecializationSet = new Set<number>();
  const availableSpecializations = new Map<number, any>();

  // First set: Collect specializations from filtered colleges
  colleges.forEach((college) => {
    const hasStream = college.streams?.some((stream: any) => 
      stream.id === filterOptions.stream.id
    );
    
    const hasCourse = college.course?.some((course: any) => 
      course.id === filterOptions.course.id
    );

    if (hasStream && hasCourse) {
      college.specializations?.forEach((specialization: any) => {
        if (specialization?.id) {
          primarySpecializationSet.add(specialization.id);
          availableSpecializations.set(specialization.id, specialization);
        }
      });
    }
  });

  // Second set: Process secondary stream data
  if (filterSecStreamData && Array.isArray(filterSecStreamData)) {
    // Find the course in filterSecStreamData
    const courseData = filterSecStreamData.find((item: any) => 
      item.courses?.some((course: any) => course.id === filterOptions.course.id)
    );

    if (courseData?.specializations) {
      courseData.specializations.forEach((specialization: any) => {
        if (specialization?.id) {
          // Only add if it exists in the primary set
          if (primarySpecializationSet.has(specialization.id)) {
            availableSpecializations.set(specialization.id, {
              ...specialization,
              hasData: true
            });
          }
        }
      });
    }
  }

  // Convert map to array and sort by specialization name
  return [...availableSpecializations.values()]
    .sort((a, b) => 
      (a.specialization_name ?? '').localeCompare(b.specialization_name ?? '')
    );
};

const checkLocationMatch = (
  college: any,
  filterKey: string,
  filterValue: any
): boolean => {
  switch (filterKey) {
    case "city":
      return college.city?.city_url === filterValue?.city_url;
    case "state":
      return college.state?.state_name === filterValue?.state_name;
    case "country":
      return filterValue?.country_name === "Abroad"
        ? college.country?.country_url !== "study-in-india"
        : college.country?.country_url === filterValue?.country_url;
    default:
      return true;
  }
};

export const updateSpecializations = (
  filterData: any[],
  filterOptions: any,
  filterSecStreamData: any
): any[] => { 
  // Return empty array if filterOptions is null/undefined
  if (!filterOptions) {
    return [];
  }

  // Apply location filters first
  const PRIORITY_KEYS = ["city", "state", "country"];
  const activeFilterKey = PRIORITY_KEYS.find((key) => filterOptions[key]) ?? "";
  const activeFilterValue = filterOptions[activeFilterKey];

  const filteredColleges = filterData.filter((college: any) => 
    checkLocationMatch(college, activeFilterKey, activeFilterValue)
  );

  // Get specializations with the enhanced logic
  const specializations = getUniqueSpecialization(
    filteredColleges,
    filterOptions,
    filterSecStreamData
  );

  // Additional filtering based on stream and course
  if (filterOptions.stream && filterOptions.course) {
    return specializations.filter((spec: any) => {
      // Check if the specialization exists in the filtered colleges
      return filteredColleges.some((college: any) => {
        const hasStream = college.streams?.some((stream: any) => 
          stream.id === filterOptions.stream.id
        );
        const hasCourse = college.course?.some((course: any) => 
          course.id === filterOptions.course.id
        );
        const hasSpecialization = college.specializations?.some((s: any) => 
          s.id === spec.id
        );
        return hasStream && hasCourse && hasSpecialization;
      });
    });
  }

  return specializations;
};
